import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FileText,
  BookUser,
  Check,
  AlertTriangle,
  CalendarCheck,
  Loader2,
  Ban,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import type { WireArticle, WireWorkflowStage, GroupMember, RoleAssignments } from '../../api/types'
import { useQualityControlMembers, useLayoutAndDesignMembers, useEditorialManagers, usePostProductionMembers } from '../../api/hooks'
import { CardShelf } from './CardShelf'

// ─── Delete confirm dialog ───────────────────────────────────────────────────

function DeleteConfirmDialog({
  article,
  open,
  onOpenChange,
  onConfirm,
}: {
  article: WireArticle | null
  open: boolean
  onOpenChange: (v: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="delete-draft-desc"
        >
          <Dialog.Title className="text-sm font-semibold text-fg">
            Delete draft?
          </Dialog.Title>
          <Dialog.Description id="delete-draft-desc" className="mt-2 text-xs text-fg-secondary">
            <span className="font-semibold text-fg line-clamp-2">{article?.title}</span>{' '}
            will be permanently deleted. This cannot be undone.
          </Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-600"
            >
              Delete draft
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ─── Column definitions ───────────────────────────────────────────────────────

type ColumnDef = {
  stage: WireWorkflowStage
  label: string
  hint: string
  /** Tailwind class for the 3px left accent strip and dot */
  barClass: string
  /** Radix CSS variable names — resolve automatically in dark mode */
  bgVar: string
  borderVar: string
  fgVar: string
  emptyText: string
}

const COLUMNS: ColumnDef[] = [
  {
    stage: 'qc_review',
    label: 'QC Review',
    hint: 'Content & artifact verification',
    barClass: 'bg-yellow-400',
    bgVar: '--amber-2',
    borderVar: '--amber-6',
    fgVar: '--amber-11',
    emptyText: 'No articles in QC.',
  },
  {
    stage: 'layout_review',
    label: 'Layout',
    hint: 'Presentation & formatting',
    barClass: 'bg-blue-400',
    bgVar: '--blue-2',
    borderVar: '--blue-6',
    fgVar: '--blue-11',
    emptyText: 'No articles in layout.',
  },
  {
    stage: 'editorial_approval',
    label: 'Editorial',
    hint: 'Manager approve / reject',
    barClass: 'bg-violet-400',
    bgVar: '--violet-2',
    borderVar: '--violet-6',
    fgVar: '--violet-11',
    emptyText: 'No articles pending approval.',
  },
  {
    stage: 'scheduled',
    label: 'Scheduled',
    hint: 'Queued for publication',
    barClass: 'bg-cyan-400',
    bgVar: '--teal-2',
    borderVar: '--teal-6',
    fgVar: '--teal-11',
    emptyText: 'Nothing scheduled.',
  },
  {
    stage: 'post_pub_review',
    label: 'Post-Pub Review',
    hint: 'Post-publication verification',
    barClass: 'bg-orange-400',
    bgVar: '--orange-2',
    borderVar: '--orange-6',
    fgVar: '--orange-11',
    emptyText: 'No articles in post-pub review.',
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24))
}

const ASSIGNABLE_STAGES = new Set(['qc_review', 'layout_review', 'editorial_approval', 'post_pub_review'])

function getDisplayStage(article: WireArticle): WireWorkflowStage {
  const rawStage = article.workflow.stage as string
  if (rawStage !== 'blocked') return article.workflow.stage

  // Compatibility fallback when backend still emits stage='blocked'.
  // Infer a canonical lane from cycle progress and assignments.
  const cycle = article.workflow.cycles?.[article.workflow.currentCycle - 1]
  if (article.workflow.scheduledFor || cycle?.editorialApproval) return 'scheduled'
  if (cycle?.qcSignoff && cycle?.layoutSignoff) return 'editorial_approval'
  if (cycle?.qcSignoff) return 'layout_review'
  if (cycle?.layoutSignoff) return 'qc_review'
  if (article.workflow.postPubAssignedTo) return 'post_pub_review'
  if (article.workflow.editorialAssignedTo) return 'editorial_approval'
  if (article.workflow.layoutAssignedTo) return 'layout_review'
  return 'qc_review'
}

function getStageAssignee(article: WireArticle): string | null {
  const stage = getDisplayStage(article)
  if (stage === 'qc_review')          return article.workflow.qcAssignedTo ?? null
  if (stage === 'layout_review')      return article.workflow.layoutAssignedTo ?? null
  if (stage === 'editorial_approval') return article.workflow.editorialAssignedTo ?? null
  if (stage === 'post_pub_review')    return article.workflow.postPubAssignedTo ?? null
  return null
}

function sortColumn(articles: WireArticle[], currentUserSub: string): WireArticle[] {
  return [...articles].sort((a, b) => {
    const aAssignee = getStageAssignee(a)
    const bAssignee = getStageAssignee(b)
    // 1. Mine first
    const aIsMe = aAssignee === currentUserSub ? 0 : 1
    const bIsMe = bAssignee === currentUserSub ? 0 : 1
    if (aIsMe !== bIsMe) return aIsMe - bIsMe
    // 2. Unassigned last
    const aUnassigned = aAssignee ? 0 : 1
    const bUnassigned = bAssignee ? 0 : 1
    if (aUnassigned !== bUnassigned) return aUnassigned - bUnassigned
    // 3. Oldest first (longest in stage = most urgent)
    const aCycle = a.workflow.cycles?.[a.workflow.currentCycle - 1]
    const bCycle = b.workflow.cycles?.[b.workflow.currentCycle - 1]
    return new Date(aCycle?.startedAt ?? a.updatedAt).getTime()
         - new Date(bCycle?.startedAt ?? b.updatedAt).getTime()
  })
}

// ─── Assign dialog ────────────────────────────────────────────────────────────

function MemberRow({
  member,
  isSelected,
  isMe,
  onToggle,
}: {
  member: GroupMember
  isSelected: boolean
  isMe: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-fill"
    >
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
        style={isSelected
          ? { backgroundColor: 'var(--blue-4)', color: 'var(--blue-11)' }
          : { backgroundColor: 'var(--gray-3)', color: 'var(--gray-11)' }
        }
        aria-hidden="true"
      >
        {(member.name ? member.name.split(' ').map((n) => n[0]).join('').slice(0, 2) : member.username.slice(0, 2)).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-semibold text-fg">
          {member.name || member.username}
          {isMe && <span className="ml-1 text-[10px] font-normal text-fg-secondary">(you)</span>}
        </p>
        {member.agency && (
          <p className="truncate text-[10px] text-fg-secondary">{member.agency}</p>
        )}
      </div>
      {isSelected && <Check size={11} style={{ color: 'var(--blue-11)' }} aria-hidden="true" />}
    </button>
  )
}

function RoleSection({
  label,
  accentClass,
  members,
  isPending,
  isError,
  selectedSub,
  currentUserSub,
  onToggle,
}: {
  label: string
  accentClass: string
  members: GroupMember[]
  isPending: boolean
  isError: boolean
  selectedSub: string | null
  currentUserSub: string
  onToggle: (sub: string) => void
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5 border-b border-line px-3 py-2">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${accentClass}`} aria-hidden="true" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-fg">{label}</span>
        <span
          className="ml-auto shrink-0 text-[10px]"
          style={{ color: selectedSub ? 'var(--blue-11)' : 'var(--amber-11)' }}
        >
          {selectedSub
            ? selectedSub === currentUserSub ? 'You' : `···${selectedSub.slice(-5)}`
            : 'Unassigned'
          }
        </span>
      </div>
      <div className="max-h-36 overflow-y-auto">
        {isPending && (
          <div className="flex justify-center py-4 text-fg-secondary">
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          </div>
        )}
        {isError && <p className="px-3 py-3 text-[10px] text-error-fg">Failed to load members.</p>}
        {!isPending && !isError && members.length === 0 && (
          <p className="px-3 py-3 text-[10px] text-fg-secondary">No members.</p>
        )}
        {members.map((member) => (
          <MemberRow
            key={member.username}
            member={member}
            isSelected={member.username === selectedSub}
            isMe={member.username === currentUserSub}
            onToggle={() => onToggle(member.username)}
          />
        ))}
      </div>
    </div>
  )
}

function AssignDialog({
  article,
  open,
  onOpenChange,
  onSave,
  currentUserSub,
}: {
  article: WireArticle | null
  open: boolean
  onOpenChange: (v: boolean) => void
  onSave: (assignments: RoleAssignments) => void
  currentUserSub: string
}) {
  const [qc,        setQc]        = useState<string | null>(null)
  const [layout,    setLayout]    = useState<string | null>(null)
  const [editorial, setEditorial] = useState<string | null>(null)
  const [postPub,   setPostPub]   = useState<string | null>(null)

  useEffect(() => {
    if (open && article) {
      setQc(article.workflow.qcAssignedTo        ?? null)
      setLayout(article.workflow.layoutAssignedTo    ?? null)
      setEditorial(article.workflow.editorialAssignedTo ?? null)
      setPostPub(article.workflow.postPubAssignedTo   ?? null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article?.id])

  const qcQ        = useQualityControlMembers()
  const layoutQ    = useLayoutAndDesignMembers()
  const editorialQ = useEditorialManagers()
  const postPubQ   = usePostProductionMembers()

  function toggle(setter: React.Dispatch<React.SetStateAction<string | null>>, sub: string) {
    setter((prev) => (prev === sub ? null : sub))
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[520px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas shadow-xl focus:outline-none"
          aria-describedby={undefined}
        >
          <div className="border-b border-line px-4 py-3">
            <Dialog.Title className="text-sm font-semibold text-fg">Assign article</Dialog.Title>
            {article && (
              <p className="mt-0.5 line-clamp-1 text-[11px] text-fg-secondary">{article.title}</p>
            )}
          </div>

          <div className="grid grid-cols-2 divide-x divide-y divide-line">
            <RoleSection
              label="QC Review"
              accentClass="bg-yellow-400"
              members={qcQ.data ?? []}
              isPending={qcQ.isPending}
              isError={qcQ.isError}
              selectedSub={qc}
              currentUserSub={currentUserSub}
              onToggle={(sub) => toggle(setQc, sub)}
            />
            <RoleSection
              label="Layout & Design"
              accentClass="bg-blue-400"
              members={layoutQ.data ?? []}
              isPending={layoutQ.isPending}
              isError={layoutQ.isError}
              selectedSub={layout}
              currentUserSub={currentUserSub}
              onToggle={(sub) => toggle(setLayout, sub)}
            />
            <RoleSection
              label="Editorial Approval"
              accentClass="bg-violet-400"
              members={editorialQ.data ?? []}
              isPending={editorialQ.isPending}
              isError={editorialQ.isError}
              selectedSub={editorial}
              currentUserSub={currentUserSub}
              onToggle={(sub) => toggle(setEditorial, sub)}
            />
            <RoleSection
              label="Post-Pub Review"
              accentClass="bg-orange-400"
              members={postPubQ.data ?? []}
              isPending={postPubQ.isPending}
              isError={postPubQ.isError}
              selectedSub={postPub}
              currentUserSub={currentUserSub}
              onToggle={(sub) => toggle(setPostPub, sub)}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={() => {
                onSave({ qcAssignedTo: qc, layoutAssignedTo: layout, editorialAssignedTo: editorial, postPubAssignedTo: postPub })
                onOpenChange(false)
              }}
              className="rounded px-3 py-1.5 text-xs font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--blue-9)' }}
            >
              Save assignments
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}


// ─── Age badge ────────────────────────────────────────────────────────────────

function AgeBadge({ startedAt }: { startedAt: string | undefined }) {
  if (!startedAt) return null
  const days = daysAgo(startedAt)
  const label = days === 0 ? 'Today' : `${days}d`

  if (days < 3) {
    return <span className="text-[10px] tabular-nums text-fg-secondary">{label}</span>
  }
  if (days < 7) {
    return (
      <span
        className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
        style={{ backgroundColor: 'var(--amber-3)', color: 'var(--amber-11)' }}
      >
        {label}
      </span>
    )
  }
  return (
    <span
      className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
      style={{ backgroundColor: 'var(--red-3)', color: 'var(--red-11)' }}
    >
      <AlertTriangle size={9} aria-hidden="true" />
      {label}
    </span>
  )
}

// ─── Blocked banner ──────────────────────────────────────────────────────────

function BlockedBanner({ article }: { article: WireArticle }) {
  const block = article.workflow.activeBlock
  if (!block) return null
  const label = block.reason === 'waiting_for_author' ? 'Waiting for Author' : 'Blocked'
  return (
    <div
      className="mb-2.5 flex items-start gap-1.5 rounded-md px-2.5 py-2 text-[10px] leading-snug"
      style={{ backgroundColor: 'var(--amber-2)', color: 'var(--amber-11)', border: '1px solid var(--amber-5)' }}
    >
      <Ban size={11} className="mt-px shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <span className="font-bold">{label}</span>
        {block.additionalInfo && (
          <p className="mt-0.5 line-clamp-2 opacity-80">{block.additionalInfo}</p>
        )}
      </div>
    </div>
  )
}

// ─── Cycle / rejection banner ─────────────────────────────────────────────────

function CycleBanner({ article }: { article: WireArticle }) {
  const { currentCycle, cycles } = article.workflow
  if (currentCycle <= 1 || !cycles) return null

  // Don't show error state if the current cycle was successfully approved
  const currentCycleData = cycles[currentCycle - 1]
  if (currentCycleData?.editorialApproval) return null

  const prevCycle = cycles[currentCycle - 2]
  const note = prevCycle?.closedBy?.note

  return (
    <div
      className="mb-2.5 flex items-start gap-1.5 rounded-md px-2.5 py-2 text-[10px] leading-snug"
      style={{ backgroundColor: 'var(--red-2)', color: 'var(--red-11)' }}
    >
      <AlertTriangle size={11} className="mt-px shrink-0" aria-hidden="true" />
      <div className="min-w-0">
        <span className="font-bold">Cycle {currentCycle}</span>
        {note && <p className="mt-0.5 line-clamp-2 opacity-80">{note}</p>}
      </div>
    </div>
  )
}

// ─── Pipeline card ────────────────────────────────────────────────────────────

type PipelineCardProps = {
  article: WireArticle
  currentUserSub: string
  barClass: string
  memberMap: Map<string, string>
  onCardClick: () => void
}

function PipelineCard({ article, currentUserSub, barClass, memberMap, onCardClick }: PipelineCardProps) {
  const { workflow } = article
  const cycle = workflow.cycles?.[workflow.currentCycle - 1]
  const Icon = article.productType === 'biographic' ? BookUser : FileText

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-line bg-surface shadow-sm transition-all hover:shadow-md hover:border-line-strong cursor-pointer"
      onClick={onCardClick}
    >
      {/* Left accent strip */}
      <div className={`absolute inset-y-0 left-0 w-[3px] ${barClass}`} aria-hidden="true" />

      <div className="pl-3.5 pr-3 pt-3 pb-2.5">
        {/* Blocked banner */}
        <BlockedBanner article={article} />

        {/* Rejection banner for cycle > 1 */}
        <CycleBanner article={article} />

        {/* Title row */}
        <div className="mb-1 flex items-start justify-between gap-2">
          <Link
            to={`/admin/articles/${article.id}`}
            className="group flex min-w-0 items-start gap-2"
          >
            <span className="mt-[2px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border border-line bg-canvas text-fg-secondary">
              <Icon size={10} aria-hidden="true" />
            </span>
            <span className="line-clamp-2 text-[12px] font-semibold leading-snug text-fg transition-colors group-hover:underline">
              {article.title}
            </span>
          </Link>
          <div className="shrink-0 pt-px">
            <AgeBadge startedAt={cycle?.startedAt} />
          </div>
        </div>

        {/* Doc ID + classification */}
        {(article.documentId || article.classification) && (
          <p className="mb-3 flex items-center gap-1.5 pl-6 text-[10px] text-fg-secondary">
            {article.documentId && <span className="font-mono">{article.documentId}</span>}
            {article.documentId && article.classification && (
              <span className="opacity-30">·</span>
            )}
            {article.classification && (
              <span className="font-mono font-semibold">
                ({[article.classification, ...(article.disseminationControls ?? [])].join('//')})
              </span>
            )}
          </p>
        )}

        {/* Scheduled time — shown instead of doc row spacer for scheduled articles */}
        {workflow.stage === 'scheduled' && workflow.scheduledFor && (
          <p className="mb-3 flex items-center gap-1 pl-6 text-[10px] font-semibold" style={{ color: 'var(--teal-11)' }}>
            <CalendarCheck size={10} aria-hidden="true" />
            {new Date(workflow.scheduledFor).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        )}

        <div className="mb-2.5 border-t border-line-subtle" />

        {/* All-role assignees + sign-off status */}
        <div className="space-y-1">
          {[
            { role: 'QC',     sub: workflow.qcAssignedTo,        accentVar: '--amber-9',  signed: !!cycle?.qcSignoff },
            { role: 'Layout', sub: workflow.layoutAssignedTo,    accentVar: '--blue-9',   signed: !!cycle?.layoutSignoff },
            { role: 'Ed.',    sub: workflow.editorialAssignedTo, accentVar: '--violet-9', signed: !!cycle?.editorialApproval },
            { role: 'Post',   sub: workflow.postPubAssignedTo,   accentVar: '--orange-9', signed: false },
          ].map(({ role, sub, accentVar, signed }) => (
            <div key={role} className="flex items-center gap-2">
              <span className="flex w-12 shrink-0 items-center gap-1 text-[10px] text-fg-secondary">
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: `var(${accentVar})` }}
                  aria-hidden="true"
                />
                {role}
              </span>
              <span className="min-w-0 flex-1 truncate text-[10px] font-medium text-fg">
                {sub
                  ? (sub === currentUserSub ? 'You' : (memberMap.get(sub) ?? sub))
                  : <span className="opacity-40">—</span>}
              </span>
              {signed && (
                <Check
                  size={10}
                  className="shrink-0"
                  style={{ color: 'var(--jade-10)' }}
                  aria-label="Signed off"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Column ───────────────────────────────────────────────────────────────────

function PipelineColumn({
  col,
  articles,
  currentUserSub,
  memberMap,
  onCardClick,
}: {
  col: ColumnDef
  articles: WireArticle[]
  currentUserSub: string
  memberMap: Map<string, string>
  onCardClick: (a: WireArticle) => void
}) {
  const sorted = useMemo(
    () => sortColumn(articles, currentUserSub),
    [articles, currentUserSub],
  )

  const unassignedCount = articles.filter((a) => ASSIGNABLE_STAGES.has(getDisplayStage(a)) && !getStageAssignee(a)).length

  return (
    <div className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl border border-line bg-canvas">
      {/* Column header */}
      <div
        className="border-b px-4 py-3"
        style={{
          backgroundColor: `var(${col.bgVar})`,
          borderBottomColor: `var(${col.borderVar})`,
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${col.barClass}`} aria-hidden="true" />
            <span
              className="text-[11px] font-bold uppercase tracking-wider"
              style={{ color: `var(${col.fgVar})` }}
            >
              {col.label}
            </span>
          </div>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums"
            style={{ backgroundColor: `var(${col.borderVar})`, color: `var(${col.fgVar})` }}
          >
            {articles.length}
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <p className="text-[10px]" style={{ color: `var(${col.fgVar})`, opacity: 0.7 }}>
            {col.hint}
          </p>
          {unassignedCount > 0 && (
            <span
              className="flex items-center gap-0.5 text-[10px] font-semibold"
              style={{ color: 'var(--amber-11)' }}
              title={`${unassignedCount} unassigned`}
            >
              <AlertTriangle size={9} aria-hidden="true" />
              {unassignedCount} unassigned
            </span>
          )}
        </div>
      </div>

      {/* Card list */}
      <div className="flex-1 space-y-2.5 overflow-y-auto p-2.5" style={{ maxHeight: 'calc(100vh - 15rem)' }}>
        {sorted.length === 0 ? (
          <p className="py-10 text-center text-[11px] text-fg-secondary">{col.emptyText}</p>
        ) : (
          sorted.map((article) => (
            <PipelineCard
              key={article.id}
              article={article}
              currentUserSub={currentUserSub}
              barClass={col.barClass}
              memberMap={memberMap}
              onCardClick={() => onCardClick(article)}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Board ────────────────────────────────────────────────────────────────────

export type PipelineBoardProps = {
  articles: WireArticle[]
  currentUserSub: string
  onReassignToMe: (article: WireArticle) => void
  onDelete: (article: WireArticle) => void
  onAssign: (article: WireArticle, assignments: RoleAssignments) => void
}

export function PipelineBoard({ articles, currentUserSub, onReassignToMe, onDelete, onAssign }: PipelineBoardProps) {
  const [pendingDelete, setPendingDelete] = useState<WireArticle | null>(null)
  const [pendingAssign, setPendingAssign] = useState<WireArticle | null>(null)
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null)

  const qcQ = useQualityControlMembers()
  const layoutQ = useLayoutAndDesignMembers()
  const editorialQ = useEditorialManagers()
  const postPubQ = usePostProductionMembers()

  // Build a sub → display name map from all known role groups
  const memberMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const q of [qcQ, layoutQ, editorialQ, postPubQ]) {
      for (const m of q.data ?? []) map.set(m.username, m.name)
    }
    return map
  }, [qcQ.data, layoutQ.data, editorialQ.data, postPubQ.data])

  // Derive selected article from live data so the shelf reflects updates
  const selectedArticle = selectedArticleId
    ? (articles.find((a) => a.id === selectedArticleId) ?? null)
    : null

  const byStage = useMemo(() => {
    const map = new Map<WireWorkflowStage, WireArticle[]>()
    for (const col of COLUMNS) map.set(col.stage, [])
    for (const article of articles) {
      const bucket = map.get(getDisplayStage(article))
      if (bucket) bucket.push(article)
    }
    return map
  }, [articles])

  const totalUnassigned = COLUMNS.reduce((n, col) => {
    return n + (byStage.get(col.stage) ?? []).filter((a) => ASSIGNABLE_STAGES.has(getDisplayStage(a)) && !getStageAssignee(a)).length
  }, 0)

  function handleDeleteConfirm() {
    if (pendingDelete) {
      onDelete(pendingDelete)
      setPendingDelete(null)
      setSelectedArticleId(null)
    }
  }

  function handleAssignConfirm(assignments: RoleAssignments) {
    if (pendingAssign) {
      onAssign(pendingAssign, assignments)
      setPendingAssign(null)
    }
  }

  return (
    <div>
      {/* Board-level summary */}
      {totalUnassigned > 0 && (
        <div
          className="mb-4 flex items-center gap-2 rounded-lg border px-4 py-2.5 text-[11px] font-semibold"
          style={{
            backgroundColor: 'var(--amber-2)',
            borderColor: 'var(--amber-6)',
            color: 'var(--amber-11)',
          }}
        >
          <AlertTriangle size={13} aria-hidden="true" />
          {totalUnassigned} article{totalUnassigned !== 1 ? 's' : ''} unassigned across the pipeline
        </div>
      )}

      {/* Columns */}
      <div className="overflow-x-auto pb-6">
        <div className="mx-auto flex w-fit gap-4">
          {COLUMNS.map((col) => (
            <PipelineColumn
              key={col.stage}
              col={col}
              articles={byStage.get(col.stage) ?? []}
              currentUserSub={currentUserSub}
              memberMap={memberMap}
              onCardClick={(a) => setSelectedArticleId(a.id)}
            />
          ))}
        </div>
      </div>

      <DeleteConfirmDialog
        article={pendingDelete}
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null) }}
        onConfirm={handleDeleteConfirm}
      />

      <AssignDialog
        article={pendingAssign}
        open={pendingAssign !== null}
        onOpenChange={(open) => { if (!open) setPendingAssign(null) }}
        onSave={handleAssignConfirm}
        currentUserSub={currentUserSub}
      />

      <CardShelf
        article={selectedArticle}
        currentUserSub={currentUserSub}
        onClose={() => setSelectedArticleId(null)}
        onAssignRequest={(a) => setPendingAssign(a)}
        onDeleteRequest={(a) => setPendingDelete(a)}
        onReassignToMe={(a) => { setSelectedArticleId(null); onReassignToMe(a) }}
      />
    </div>
  )
}
